import { useQuery, useMutation } from 'react-query';
import {
  getInviteMetadata,
  getCompanies,
  validateEmail,
  getNetworkMetadata,
  postSalesforceLead,
  getIntroducerFromToken,
  postSignupQuestionnaire,
  getValidateContinueUrl,
  postUserExits,
  postInviteUser,
  postInviteSignUp,
  postInviteBundle,
  getEntity,
  getRemainingBundles,
  postSignupComplete,
  putInviteBundle,
  postCancelSubscription,
  getCompaniesHouseLookup,
  deleteIntegration,
} from 'services/auth/index';
import { CompanyVariants } from '@capitalise/common';


export const useQueryGetInviteMetadata = (token) =>
  useQuery(['invite-metadata', token], async () => {
    const result = await getInviteMetadata(token);
    const data = result?.data ?? [];
    return data;
  });

export const useQueryGetCompanies = (query, country) =>
  useQuery('invite-metadata', async () => {
    if (query.length > 2) {
      const result = await getCompanies(query, country);
      const data = result?.data ?? [];
      return data;
    }
  });

export const useQueryGetEntity = (entityId, options = {}) =>
  useQuery(
    ['entity', entityId],
    async () => {
      const result = await getEntity(entityId);
      const data = result?.data ?? {};
      return data;
    },
    { enabled: !!entityId },
  );

export const useQueryGetNetwork = (networkSlug) =>
  useQuery('invite-network-metadata', async () => {
    const result = await getNetworkMetadata(networkSlug);
    const data = result?.data ?? [];
    return data;
  });

export const useQueryGetIntroducerByToken = (token) =>
  useQuery(
    'introducer-token',
    async () => {
      const result = await getIntroducerFromToken(token);
      const data = result?.data ?? [];
      return data;
    },
    { retry: false, enabled: !!token },
  );

export const useQueryValidateContinueUrl = (url) =>
  useQuery(
    'validate-continue-url',
    async () => {
      const result = await getValidateContinueUrl(url);
      const data = result?.data ?? [];
      return data;
    },
    { retry: false },
  );

export const useMutationValidateEmail = () =>
  useMutation(async (email) => {
    const result = await validateEmail(email);
    const data = result?.data ?? [];
    return data;
  });

export const useMutationPostSalesforceLead = () =>
  useMutation(async (payload) => {
    const result = await postSalesforceLead(payload);
    const data = result?.data ?? [];
    return data;
  });

export const useMutationPostQuestionaireSignup = () =>
  useMutation(async ({ challengeCode, ...payload }) => {
    const result = await postSignupQuestionnaire(payload, challengeCode);
    const data = result?.data ?? [];
    return data;
  });

export const useMutationPostUserExists = () =>
  useMutation(async (email) => {
    const result = await postUserExits({ emails: [email] });
    const data = result?.data ?? [];
    return data;
  });

export const useMutationPostInviteUser = () =>
  useMutation(async (body) => {
    const result = await postInviteUser(body);
    const data = result?.data ?? [];
    return data;
  });

export const useMutationPostInviteSignUp = () =>
  useMutation(async ({ type, payload }) => {
    const result = await postInviteSignUp(type, payload);
    const data = result?.data ?? {};
    return data;
  });

export const useGetBundle = () =>
  useQuery('subscriptions-bundle', async () => {
    const result = await getRemainingBundles();
    const data = result?.data ?? {};
    return data;
  });

export const useMutationPostInviteBundle = () =>
  useMutation(async (payload) => {
    const result = await postInviteBundle(payload);
    const data = result?.data ?? {};
    return data;
  });

export const useMutationPostSignupComplete = () =>
  useMutation(async ({ payload }) => {
    const result = await postSignupComplete(payload);
  });

export const useMutationPutInviteBundle = () =>
  useMutation(async (payload) => {
    const result = await putInviteBundle(payload);
    const data = result?.data ?? {};
    return data;
  });

export const useMutationPostCancelBundle = () =>
  useMutation(async (payload) => {
    const result = await postCancelSubscription(payload);
    const data = result?.data ?? {};
    return data;
  });

export const useMutationDeleteIntegration = () =>
  useMutation(async ({ provider, entityId }) => {
    await deleteIntegration({ provider, entityId });
  });
